import { Box, Button, Dialog, DialogContent, makeStyles } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DialogTitleBlock from '../../../../../../../components/Modals/Dialog/DialogTitleBlock';
import { useAuth } from '../../../../../../../contexts/AuthContext/AuthContext';
import { useBenchmarkState } from '../../../../../../Benchmark/Context/BenchmarkContext';
import { useCompaniesState } from '../../../../../shared/CompanyContext/CompanyContext';
import { useErrorsDispatch } from '../../../../../shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../../shared/ErrorContext/ErrorContext.types';
import CreateBenchmarkModal from './CreateBenchmarkModal';
import RefineBenchmarkModal from './RefineBenchmarkModal';

export interface Props {
  benchmarkRefinement: boolean;
  totalCount: number;
}

const BENCHMARK_LIMIT_DEFAULT = 500;

const SendBenchmark: React.FC<Props> = ({ benchmarkRefinement, totalCount }) => {
  const classes = useStyles(benchmarkRefinement);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { activeBenchmark } = useBenchmarkState();
  const dispatchErrors = useErrorsDispatch();
  const { user } = useAuth();
  const { selectedCompanies } = useCompaniesState();

  const openModal = () => {
    const limit =
      user && user.searchResultLimit !== undefined
        ? user.searchResultLimit
        : BENCHMARK_LIMIT_DEFAULT;
    if (benchmarkRefinement && toBenchmarkRecordsCount() > limit) {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error: { statusText: 'BENCHMARK_COMPANY_LIMIT_EXCEEDED' }, count: limit },
      });
    } else {
      setOpen(true);
    }
  };

  const toBenchmarkRecordsCount = () => {
    return selectedCompanies.length > 0 ? selectedCompanies.length : totalCount;
  };

  return (
    <Box className={`${classes.rightBox}`}>
      <Button
        className={classes.button}
        aria-controls="customized-menu"
        ref={anchorRef}
        aria-haspopup="true"
        variant="contained"
        color="primary"
        onClick={openModal}
      >
        {benchmarkRefinement
          ? t('benchmark:sendToBenchmark.refineBenchmarking') +
            ' (' +
            toBenchmarkRecordsCount() +
            ')'
          : t('benchmark:sendToBenchmark.modalTitle') + ' (' + toBenchmarkRecordsCount() + ')'}
      </Button>
      <Dialog
        maxWidth="md"
        fullWidth={true}
        onClose={() => {
          setOpen(false);
        }}
        open={open}
      >
        <DialogTitleBlock
          icon={<SearchIcon />}
          title={
            benchmarkRefinement
              ? `${t('benchmark:sendToBenchmark.refinementSummary')} "${activeBenchmark?.name}"`
              : t('benchmark:sendToBenchmark.modalTitle')
          }
          handleClose={() => setOpen(false)}
        />
        <DialogContent dividers>
          {benchmarkRefinement ? (
            <RefineBenchmarkModal setOpen={setOpen} />
          ) : (
            <CreateBenchmarkModal setOpen={setOpen} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  rightBox: {
    display: 'flex',
  },
  button: {
    background: theme.palette.primary.main,
    '&:hover': {
      background: theme.palette.primary.main,
    },
    borderRadius: 0,
    lineHeight: '95%',
  },
}));

export default SendBenchmark;
